import {
  AfterViewChecked,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { StepDialogComponent } from '../step-dialog/step-dialog.component';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { TeamsService } from '../../../services/teams/teams.service';
import { UsersService } from '../../../services/users/users.service';
import { StripeService } from '../../../services/stripe/stripe.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import {
  PAYCOR_PROVIDER_SLUG,
  PaycorLegalEntityMetadata,
  PayrollProviderConnectionResponse,
  PayrollService,
  UKG_READY_PROVIDER_SLUG,
} from '../../../services/payroll/payroll.service';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { PAYROLL_ADMIN_PAYCOR_CONNECTION_SETUP_FLAG } from '../payroll-admin-feature-flags';

export type PayrollConnectionDialogData = {
  company?: any;
  initialTab?: 'payroll' | 'bank';
  parentCompanyId?: string;
  hasExistingBankConnection?: boolean;
  providerConnection?: PayrollProviderConnectionResponse | null;
};

export type PayrollConnectionDialogResult =
  | { saved: false }
  | { saved: true; companyId?: string; bankConnected?: boolean };

const DUPLICATE_DATA_HELP_MESSAGE = 'This information has already been used. Please contact help@wallit.app to connect your account to the existing company.';
const BANK_OWNER_REQUIRED_MESSAGE = 'Stripe requires the full name and email associated with the bank account you are connecting. This may be different from the signed-in admin.';
const BANK_OWNER_FIX_MESSAGE = 'Use the full name and email associated with the bank account you are connecting, then try again.';
const BANK_FORM_INCOMPLETE_MESSAGE = 'Complete the Stripe bank connection before saving.';
const BANK_FORM_LOADING_MESSAGE = 'The Stripe bank connection form is still loading. Please try again.';
const GENERIC_BANK_CONNECTION_ERROR_MESSAGE = 'Unable to connect bank account.';
const COMPANY_PROVISIONING_REQUIRED_MESSAGE = 'A Wallit administrator must provision the company and assign your Payroll Admin access before you can configure a connection.';
const UKG_READY_BASE_URL = 'https://secure5.us.rdy.ukg.dev/ta/rest';
const PAYCOR_SANDBOX_ENVIRONMENT = 'sandbox';
const UKG_READY_SETUP_PROVIDER = { label: 'UKG Ready', value: UKG_READY_PROVIDER_SLUG } as const;
const PAYCOR_SETUP_PROVIDER = { label: 'Paycor', value: PAYCOR_PROVIDER_SLUG } as const;

type PayrollSetupSnapshot = {
  clientId: string;
  clientSecret: string;
  companyShortName: string;
  environment: string;
  legalEntityId: string;
  legalEntityName: string;
  name: string;
  providerSlug: string;
  refreshToken: string;
  subscriptionKey: string;
  tenantId: string;
};

@Component({
    selector: 'app-payroll-connection-dialog',
    templateUrl: './payroll-connection-dialog.component.html',
    styleUrls: ['./payroll-connection-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollConnectionDialogComponent extends StepDialogComponent implements AfterViewChecked, OnDestroy {
  @ViewChild('bankPaymentElementHost') bankPaymentElementHost?: ElementRef<HTMLDivElement>;

  isSaving = false;
  isBankElementBusy = false;
  hasBankElementInput = false;
  isBankElementComplete = false;
  bankOwnerErrorMessage = '';
  bankConnectionErrorMessage = '';
  clientSecretVisible = false;
  subscriptionKeyVisible = false;
  refreshTokenVisible = false;
  isDiscoveringPaycorLegalEntities = false;
  paycorLegalEntities: PaycorLegalEntityMetadata[] = [];
  paycorLegalEntityDiscoveryMessage = '';
  paycorLegalEntityDiscoveryError = '';

  private elements: any;
  private bankPaymentElement: any;
  private bankPaymentElementMounted = false;
  private bankPaymentElementMounting = false;
  private lastStepIndex = -1;
  private formReady = false;
  private initialPayrollSnapshot: PayrollSetupSnapshot | null = null;
  private hasExistingPayrollCredential = false;
  private isApplyingPaycorLegalEntityPatch = false;
  private preparedClientSecret = '';

  private activeCompany: any;
  public activeTeam: any;
  public companyTeams: any[] = [];
  public selectedTeamIds: string[] = [];
  public isPaycorConnectionSetupEnabled = false;

  readonly payrollForm = this.fb.group({
    name: ['', [Validators.required]],
    providerSlug: [UKG_READY_PROVIDER_SLUG, [Validators.required]],
    companyShortName: ['', [Validators.required]],
    clientId: ['', [Validators.required]],
    clientSecret: ['', [Validators.required]],
    environment: [PAYCOR_SANDBOX_ENVIRONMENT],
    subscriptionKey: [''],
    refreshToken: [''],
    legalEntityId: [''],
    tenantId: [''],
    legalEntityName: [''],
  });
  readonly bankContactForm = this.fb.group({
    fullName: ['', [Validators.required, PayrollConnectionDialogComponent.trimmedRequiredValidator]],
    email: ['', [Validators.required, Validators.email]],
  });

  constructor(
    private ref: MatDialogRef<PayrollConnectionDialogComponent, PayrollConnectionDialogResult>,
    private fb: FormBuilder,
    private teamCompaniesService: TeamCompaniesService,
    private payrollService: PayrollService,
    private teamsService: TeamsService,
    private usersService: UsersService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private stripeService: StripeService,
    private spinnerService: SpinnerService,
    private snackBar: MatSnackBar,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: PayrollConnectionDialogData
  ) {
    super();

    this.initSteps(
      [
        { id: 'payroll', label: 'Payroll Connection', icon: 'account_balance' },
        { id: 'bank', label: 'Bank Connection', icon: 'credit_card' },
      ],
      'tabs',
      data?.initialTab === 'bank' ? 1 : 0
    );

    this.activeCompany = data?.company ?? null;

    this.bankContactForm.patchValue(
      { email: this.usersService.getCurrentEmail() || '' },
      { emitEvent: false }
    );
    this.bankContactForm.valueChanges.subscribe(() => {
      this.bankOwnerErrorMessage = '';
    });
    this.payrollForm.get('providerSlug')?.valueChanges.subscribe(() => {
      this.syncPayrollFormValidators();
      this.clientSecretVisible = false;
      this.subscriptionKeyVisible = false;
      this.refreshTokenVisible = false;
      this.clearPaycorLegalEntityDiscovery();
    });
    ['clientId', 'clientSecret', 'subscriptionKey', 'refreshToken'].forEach(controlName => {
      this.payrollForm.get(controlName)?.valueChanges.subscribe(() => {
        this.clearPaycorLegalEntityDiscovery();
      });
    });
    this.payrollForm.get('legalEntityId')?.valueChanges.subscribe(value => {
      this.syncPaycorLegalEntityFromManualId(value);
    });

    this.patchFromCompany(this.activeCompany);
    this.loadPaycorConnectionSetupFeatureFlag();
    this.resolveCompanyTeams();
    this.formReady = true;
  }

  private static trimmedRequiredValidator(control: AbstractControl): ValidationErrors | null {
    return `${control?.value ?? ''}`.trim() ? null : { required: true };
  }

  ngAfterViewChecked(): void {
    if (!this.formReady) {
      return;
    }

    if (this.lastStepIndex !== this.stepIndex) {
      if (this.lastStepIndex === 1) {
        this.unmountBankElement();
      }
      this.lastStepIndex = this.stepIndex;
    }

    if (this.stepIndex === 1) {
      this.mountBankElementIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.unmountBankElement();
  }

  close(): void {
    this.ref.close({ saved: false });
  }

  get title(): string {
    return this.activeCompany?.id ? 'Edit Connection' : 'New Connection';
  }

  get hasActiveCompany(): boolean {
    return !!this.activeCompany?.id;
  }

  get canSave(): boolean {
    if (this.isSaving || !this.hasActiveCompany) {
      return false;
    }

    const payrollChanged = this.hasPayrollChanges();
    const canSavePayrollOnly = this.payrollForm.valid && payrollChanged;

    if (this.stepIndex === 0) {
      return canSavePayrollOnly;
    }

    if (this.hasExistingBankConnection) {
      return canSavePayrollOnly;
    }

    const canSaveBank = this.hasBankElementInput ? this.isBankElementComplete : false;
    return !this.isBankElementBusy && (canSavePayrollOnly || canSaveBank);
  }

  get fullNameControl(): AbstractControl | null {
    return this.bankContactForm.get('fullName');
  }

  get emailControl(): AbstractControl | null {
    return this.bankContactForm.get('email');
  }

  get selectedPayrollProviderSlug(): string {
    return this.normalizeSetupProviderSlug(this.payrollForm.getRawValue().providerSlug);
  }

  get isPaycorProvider(): boolean {
    return this.selectedPayrollProviderSlug === PAYCOR_PROVIDER_SLUG;
  }

  get isUkgReadyProvider(): boolean {
    return this.selectedPayrollProviderSlug === UKG_READY_PROVIDER_SLUG;
  }

  get shouldShowProviderSelector(): boolean {
    return !this.data?.providerConnection;
  }

  get supportedPayrollSetupProviders(): Array<{ label: string; value: string }> {
    const providers: Array<{ label: string; value: string }> = [UKG_READY_SETUP_PROVIDER];
    if (this.isPaycorConnectionSetupEnabled || this.selectedPayrollProviderSlug === PAYCOR_PROVIDER_SLUG) {
      providers.push(PAYCOR_SETUP_PROVIDER);
    }
    return providers;
  }

  get clientSecretPlaceholder(): string {
    if (this.isPaycorProvider) {
      return this.hasExistingPayrollCredential
        ? 'Leave blank to keep existing Paycor client secret'
        : 'Enter Paycor client secret';
    }

    return this.hasExistingPayrollCredential
      ? 'Leave blank to keep existing client secret'
      : 'Enter client secret';
  }

  get clientSecretInputType(): 'password' | 'text' {
    return this.clientSecretVisible ? 'text' : 'password';
  }

  get clientSecretToggleLabel(): string {
    return this.clientSecretVisible ? 'Hide client secret' : 'View client secret';
  }

  get subscriptionKeyInputType(): 'password' | 'text' {
    return this.subscriptionKeyVisible ? 'text' : 'password';
  }

  get subscriptionKeyToggleLabel(): string {
    return this.subscriptionKeyVisible ? 'Hide subscription key' : 'View subscription key';
  }

  get refreshTokenInputType(): 'password' | 'text' {
    return this.refreshTokenVisible ? 'text' : 'password';
  }

  get refreshTokenToggleLabel(): string {
    return this.refreshTokenVisible ? 'Hide refresh token' : 'View refresh token';
  }

  get canPreserveClientSecret(): boolean {
    return this.hasExistingPayrollCredential;
  }

  get canPreservePaycorSecrets(): boolean {
    return this.isPaycorProvider && this.hasExistingPayrollCredential;
  }

  toggleClientSecretVisibility(): void {
    this.clientSecretVisible = !this.clientSecretVisible;
  }

  toggleSubscriptionKeyVisibility(): void {
    this.subscriptionKeyVisible = !this.subscriptionKeyVisible;
  }

  toggleRefreshTokenVisibility(): void {
    this.refreshTokenVisible = !this.refreshTokenVisible;
  }

  async discoverPaycorLegalEntities(): Promise<void> {
    if (!this.isPaycorProvider || this.isDiscoveringPaycorLegalEntities) {
      return;
    }

    if (!this.activeCompany?.id) {
      this.paycorLegalEntityDiscoveryError = COMPANY_PROVISIONING_REQUIRED_MESSAGE;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.paycorLegalEntityDiscoveryError,
      });
      return;
    }

    const requiredControls = ['clientId', 'clientSecret', 'subscriptionKey', 'refreshToken'];
    const missingRequiredControl = requiredControls.some(controlName => {
      const control = this.payrollForm.get(controlName);
      control?.markAsTouched();
      return !!control?.hasError('required');
    });
    if (missingRequiredControl) {
      this.paycorLegalEntityDiscoveryError = 'Complete all required Paycor sandbox connection fields before discovering legal entities.';
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.paycorLegalEntityDiscoveryError,
      });
      return;
    }

    this.isDiscoveringPaycorLegalEntities = true;
    this.paycorLegalEntityDiscoveryError = '';
    this.paycorLegalEntityDiscoveryMessage = '';

    try {
      const result = await this.payrollService.discoverPaycorLegalEntities(
        this.activeCompany.id,
        this.buildPaycorSetupPayload(this.payrollForm.getRawValue()),
      );
      this.paycorLegalEntities = (result?.legalEntities || [])
        .filter(entity => !!`${entity?.legalEntityId || ''}`.trim())
        .map(entity => ({
          legalEntityId: `${entity.legalEntityId || ''}`.trim(),
          legalEntityName: `${entity.legalEntityName || ''}`.trim(),
          tenantId: `${entity.tenantId || ''}`.trim(),
        }));

      if (this.paycorLegalEntities.length === 1) {
        this.selectPaycorLegalEntity(this.paycorLegalEntities[0].legalEntityId || '');
        this.paycorLegalEntityDiscoveryMessage = 'One activated legal entity was found and selected.';
        return;
      }

      if (this.paycorLegalEntities.length > 1) {
        this.paycorLegalEntityDiscoveryMessage = 'Select one activated legal entity before saving.';
        return;
      }

      this.paycorLegalEntityDiscoveryMessage = 'No activated legal entities were returned for these credentials.';
    } catch (error: any) {
      console.error('Unable to discover Paycor legal entities', this.getSafeSaveErrorLog(error));
      this.paycorLegalEntities = [];
      this.paycorLegalEntityDiscoveryError = this.getPaycorDiscoveryErrorMessage(error);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.paycorLegalEntityDiscoveryError,
      });
    } finally {
      this.isDiscoveringPaycorLegalEntities = false;
    }
  }

  selectPaycorLegalEntity(legalEntityId: string): void {
    const entity = this.findPaycorLegalEntity(legalEntityId);
    if (!entity) {
      return;
    }
    this.patchPaycorLegalEntity(entity);
  }

  async onSave(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (!this.hasActiveCompany) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: COMPANY_PROVISIONING_REQUIRED_MESSAGE,
      });
      return;
    }

    const shouldWritePayroll = this.hasPayrollChanges();
    const canConnectBank =
      this.stepIndex === 1 &&
      !this.hasExistingBankConnection &&
      !!this.activeCompany?.id;
    const shouldConnectBank = canConnectBank;

    if (canConnectBank) {
      const bankValidationMessage = this.validateBankSectionBeforeSave();
      if (bankValidationMessage) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: bankValidationMessage,
        });
        return;
      }
    }

    if (!shouldWritePayroll && !shouldConnectBank) {
      this.close();
      return;
    }

    if (shouldWritePayroll) {
      this.payrollForm.markAllAsTouched();
      if (this.payrollForm.invalid) {
        return;
      }
    }

    this.isSaving = true;
    this.spinnerService.show('Saving connection...');

    try {
      let bankConnected = false;
      if (shouldConnectBank) {
        bankConnected = await this.connectBankAccount();
        if (!bankConnected) {
          this.spinnerService.hide();
          return;
        }
      }

      const company = shouldWritePayroll
        ? await this.upsertPayrollConnection()
        : this.activeCompany;
      if (!company) {
        this.spinnerService.hide();
        return;
      }

      if (shouldWritePayroll) {
        this.usersService.reloadMe();
      }

      this.spinnerService.hide();
      this.ref.close({ saved: true, companyId: company.id, bankConnected });
    } catch (error: any) {
      this.spinnerService.hide();
      console.error('Unable to save payroll connection', this.getSafeSaveErrorLog(error));
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.getSaveErrorMessage(error),
      });
    } finally {
      this.isSaving = false;
    }
  }

  private patchFromCompany(company: any): void {
    const ukg = company?.payrollConfig?.payroll?.ukg ?? {};
    const providerConnection = this.data?.providerConnection;
    const providerSlug = this.normalizeSetupProviderSlug(
      providerConnection?.providerSlug || providerConnection?.connection?.providerSlug || UKG_READY_PROVIDER_SLUG,
    );
    const providerSettings = providerConnection?.connection?.settings ?? {};
    const providerCredentialPayload = providerConnection?.connection?.credential?.redactedPayload ?? {};
    const companyShortName = providerSlug === UKG_READY_PROVIDER_SLUG
      ? providerSettings.companyShortName || ukg.companyShortName || ''
      : '';
    const clientId = providerSlug === PAYCOR_PROVIDER_SLUG
      ? providerSettings.clientId || ''
      : providerCredentialPayload.clientId || ukg.clientId || '';
    this.payrollForm.patchValue(
      {
        name: company?.name || '',
        providerSlug,
        companyShortName,
        clientId,
        clientSecret: '',
        environment: providerSlug === PAYCOR_PROVIDER_SLUG
          ? providerSettings.environment || PAYCOR_SANDBOX_ENVIRONMENT
          : PAYCOR_SANDBOX_ENVIRONMENT,
        subscriptionKey: '',
        refreshToken: '',
        legalEntityId: providerSlug === PAYCOR_PROVIDER_SLUG ? providerSettings.legalEntityId || '' : '',
        tenantId: providerSlug === PAYCOR_PROVIDER_SLUG ? providerSettings.tenantId || '' : '',
        legalEntityName: providerSlug === PAYCOR_PROVIDER_SLUG ? providerSettings.legalEntityName || '' : '',
      },
      { emitEvent: false }
    );
    this.syncPayrollFormValidators();
    this.initialPayrollSnapshot = this.capturePayrollSnapshot();
  }

  private syncPayrollFormValidators(): void {
    const providerSlug = this.selectedPayrollProviderSlug;
    this.hasExistingPayrollCredential = this.hasExistingCredentialForProvider(providerSlug);
    const companyShortNameControl = this.payrollForm.get('companyShortName');
    const environmentControl = this.payrollForm.get('environment');
    const clientSecretControl = this.payrollForm.get('clientSecret');
    const subscriptionKeyControl = this.payrollForm.get('subscriptionKey');
    const refreshTokenControl = this.payrollForm.get('refreshToken');

    if (!companyShortNameControl || !environmentControl || !clientSecretControl || !subscriptionKeyControl || !refreshTokenControl) {
      return;
    }

    if (providerSlug === UKG_READY_PROVIDER_SLUG) {
      companyShortNameControl.setValidators([Validators.required]);
      environmentControl.clearValidators();
      subscriptionKeyControl.clearValidators();
      refreshTokenControl.clearValidators();
    } else {
      companyShortNameControl.clearValidators();
      environmentControl.setValidators([Validators.required]);
      subscriptionKeyControl.setValidators(this.hasExistingPayrollCredential ? [] : [Validators.required]);
      refreshTokenControl.setValidators(this.hasExistingPayrollCredential ? [] : [Validators.required]);
    }

    if (this.hasExistingPayrollCredential) {
      clientSecretControl.clearValidators();
    } else {
      clientSecretControl.setValidators([Validators.required]);
    }

    companyShortNameControl.updateValueAndValidity({ emitEvent: false });
    environmentControl.updateValueAndValidity({ emitEvent: false });
    clientSecretControl.updateValueAndValidity({ emitEvent: false });
    subscriptionKeyControl.updateValueAndValidity({ emitEvent: false });
    refreshTokenControl.updateValueAndValidity({ emitEvent: false });
  }

  private hasExistingCredentialForProvider(providerSlug: string): boolean {
    const normalizedProviderSlug = this.normalizeSetupProviderSlug(providerSlug);
    const providerConnection = this.data?.providerConnection;
    const existingProviderSlug = this.normalizeSetupProviderSlug(
      providerConnection?.providerSlug || providerConnection?.connection?.providerSlug || '',
    );
    if (
      normalizedProviderSlug === existingProviderSlug
      && !!providerConnection?.connection?.credential
    ) {
      return true;
    }

    if (normalizedProviderSlug !== UKG_READY_PROVIDER_SLUG) {
      return false;
    }

    const ukg = this.activeCompany?.payrollConfig?.payroll?.ukg ?? {};
    return !!(
      providerConnection?.isCompatibilityFallback
      || ukg.clientSecret
    );
  }

  private async resolveCompanyTeams(): Promise<void> {
    if (!this.activeCompany?.id) {
      this.activeTeam = null;
      this.companyTeams = [];
      this.selectedTeamIds = [];
      return;
    }

    try {
      const userId = this.usersService.getCurrentUserId();
      const teams = await this.teamsService.getUserTeamsInfo(userId);
      const allUserTeams = Array.isArray(teams) ? teams : [];
      this.companyTeams = this.filterTeamsForCompany(allUserTeams, this.activeCompany.id);
      this.activeTeam = this.companyTeams.length > 0 ? this.companyTeams[0] : null;
      this.selectedTeamIds = this.companyTeams.map(team => team.id).filter(Boolean);
    } catch {
      this.activeTeam = null;
      this.companyTeams = [];
      this.selectedTeamIds = [];
    }
  }

  private async loadPaycorConnectionSetupFeatureFlag(): Promise<void> {
    const providerSlug = this.selectedPayrollProviderSlug;
    if (providerSlug === PAYCOR_PROVIDER_SLUG) {
      this.isPaycorConnectionSetupEnabled = true;
      return;
    }

    try {
      const me = this.usersService.me?.() || {};
      this.isPaycorConnectionSetupEnabled = await this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_PAYCOR_CONNECTION_SETUP_FLAG,
        'team-admin',
        {
          partnerId: `${me?.user?.partnerId || me?.brand?.partnerId || ''}`.trim() || undefined,
          userId: this.usersService.getCurrentUserId() || undefined,
          companyId: `${this.activeCompany?.id || ''}`.trim() || undefined,
          // Match the setup API's existing company-as-team targeting rules.
          teamId: `${this.activeCompany?.id || ''}`.trim() || undefined,
        },
      );
    } catch (error: any) {
      console.error('Failed to evaluate Paycor payroll connection setup feature flag', {
        code: error?.error?.code || error?.code || '',
        status: error?.status || error?.statusCode || '',
      });
      this.isPaycorConnectionSetupEnabled = false;
    }
  }

  private async upsertPayrollConnection(): Promise<any> {
    if (!this.activeCompany?.id) {
      throw new Error(COMPANY_PROVISIONING_REQUIRED_MESSAGE);
    }

    this.payrollForm.markAllAsTouched();
    if (this.payrollForm.invalid) {
      return null;
    }

    const formValue = this.payrollForm.getRawValue();
    const companyShortName = `${formValue.companyShortName || ''}`.trim();
    const clientId = `${formValue.clientId || ''}`.trim();
    const clientSecret = `${formValue.clientSecret || ''}`.trim();
    if (this.selectedPayrollProviderSlug === PAYCOR_PROVIDER_SLUG) {
      return this.upsertPaycorPayrollConnection(formValue);
    }

    const setupPayload = {
      baseUrl: UKG_READY_BASE_URL,
      clientId,
      ...(clientSecret ? { clientSecret } : {}),
      companyShortName,
    };

    const result = await this.payrollService.upsertUkgReadyConnectionSetup(
      this.activeCompany.id,
      setupPayload,
    );
    const updatedCompany = result?.company;
    this.activeCompany = {
      ...this.activeCompany,
      ...(updatedCompany || {}),
    };
    this.data.providerConnection = result?.connection || this.data.providerConnection || null;
    this.hasExistingPayrollCredential = true;
    this.payrollForm.patchValue({ clientSecret: '' }, { emitEvent: false });
    this.syncPayrollFormValidators();
    this.initialPayrollSnapshot = this.capturePayrollSnapshot();
    this.clientSecretVisible = false;
    return this.activeCompany;
  }

  private async upsertPaycorPayrollConnection(formValue: any): Promise<any> {
    if (!this.activeCompany?.id) {
      throw new Error('Create the payroll company before saving Paycor setup.');
    }

    const paycorPayload = this.buildPaycorSetupPayload(formValue);
    const result = await this.payrollService.upsertPaycorConnectionSetup(
      this.activeCompany.id,
      paycorPayload,
    );
    this.data.providerConnection = result?.connection || this.data.providerConnection || null;
    this.hasExistingPayrollCredential = true;
    this.payrollForm.patchValue(
      {
        clientSecret: '',
        refreshToken: '',
        subscriptionKey: '',
      },
      { emitEvent: false },
    );
    this.syncPayrollFormValidators();
    this.initialPayrollSnapshot = this.capturePayrollSnapshot();
    this.clientSecretVisible = false;
    this.subscriptionKeyVisible = false;
    this.refreshTokenVisible = false;
    return this.activeCompany;
  }

  private buildPaycorSetupPayload(formValue: any): {
    clientId: string;
    clientSecret?: string;
    environment: 'sandbox';
    legalEntityId?: string;
    legalEntityName?: string;
    refreshToken?: string;
    subscriptionKey?: string;
    tenantId?: string;
  } {
    const payload: {
      clientId: string;
      clientSecret?: string;
      environment: 'sandbox';
      legalEntityId?: string;
      legalEntityName?: string;
      refreshToken?: string;
      subscriptionKey?: string;
      tenantId?: string;
    } = {
      clientId: `${formValue.clientId || ''}`.trim(),
      environment: PAYCOR_SANDBOX_ENVIRONMENT,
    };

    const optionalTextFields: Array<keyof typeof payload> = [
      'clientSecret',
      'legalEntityId',
      'legalEntityName',
      'refreshToken',
      'subscriptionKey',
      'tenantId',
    ];
    optionalTextFields.forEach((field) => {
      const value = `${formValue[field] || ''}`.trim();
      if (value) {
        (payload as any)[field] = value;
      }
    });

    return payload;
  }

  private clearPaycorLegalEntityDiscovery(): void {
    this.paycorLegalEntities = [];
    this.paycorLegalEntityDiscoveryMessage = '';
    this.paycorLegalEntityDiscoveryError = '';
  }

  private findPaycorLegalEntity(legalEntityId: string): PaycorLegalEntityMetadata | null {
    const normalizedLegalEntityId = `${legalEntityId || ''}`.trim();
    if (!normalizedLegalEntityId) {
      return null;
    }
    return this.paycorLegalEntities.find(entity => `${entity?.legalEntityId || ''}`.trim() === normalizedLegalEntityId) || null;
  }

  private patchPaycorLegalEntity(entity: PaycorLegalEntityMetadata): void {
    this.isApplyingPaycorLegalEntityPatch = true;
    this.payrollForm.patchValue(
      {
        legalEntityId: `${entity.legalEntityId || ''}`.trim(),
        legalEntityName: `${entity.legalEntityName || ''}`.trim(),
        tenantId: `${entity.tenantId || ''}`.trim(),
      },
      { emitEvent: false },
    );
    this.isApplyingPaycorLegalEntityPatch = false;
  }

  private syncPaycorLegalEntityFromManualId(legalEntityId: any): void {
    if (this.isApplyingPaycorLegalEntityPatch || !this.isPaycorProvider || this.paycorLegalEntities.length === 0) {
      return;
    }

    const entity = this.findPaycorLegalEntity(`${legalEntityId || ''}`);
    if (entity) {
      this.patchPaycorLegalEntity(entity);
      return;
    }

    this.payrollForm.patchValue(
      {
        legalEntityName: '',
        tenantId: '',
      },
      { emitEvent: false },
    );
  }

  private async connectBankAccount(): Promise<boolean> {
    if (!this.activeCompany?.id) {
      return false;
    }

    this.isBankElementBusy = true;
    this.clearBankErrors();

    try {
      if (!this.elements || !this.bankPaymentElement) {
        this.showBankConnectionError(BANK_FORM_LOADING_MESSAGE);
        return false;
      }

      const clientSecret = this.preparedClientSecret || (await this.resolveClientSecret());
      if (!clientSecret) {
        this.showBankConnectionError('Unable to start bank setup. Please try again.');
        return false;
      }

      const submitResult = await this.elements.submit();
      if (submitResult?.error) {
        this.handleBankConnectionError(submitResult.error);
        return false;
      }

      const returnUrl = window.location.href;
      const billingName = `${this.fullNameControl?.value ?? ''}`.trim();
      const billingEmail = `${this.emailControl?.value ?? ''}`.trim();
      const result = await this.stripeService.confirmSetup({
        elements: this.elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              name: billingName,
              email: billingEmail,
            },
          },
        },
      });

      if (result?.error) {
        this.handleBankConnectionError(result.error);
        return false;
      }

      this.preparedClientSecret = '';
      const setupIntentId = result?.setupIntent?.id || '';
      const createdPaymentMethodId = result?.setupIntent?.payment_method || '';
      if (!setupIntentId || !createdPaymentMethodId) {
        this.showBankConnectionError(BANK_FORM_INCOMPLETE_MESSAGE);
        return false;
      }

      await this.teamCompaniesService.finalizePayrollAdminBankConnection(
        this.activeCompany.id,
        setupIntentId
      );

      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Bank account connected successfully.',
      });

      return true;
    } catch (error) {
      console.error('Unable to connect bank account', error);
      this.handleBankConnectionError(error);
      return false;
    } finally {
      this.isBankElementBusy = false;
    }
  }

  private mountBankElementIfNeeded(): void {
    if (this.hasExistingBankConnection) {
      return;
    }
    if (
      this.bankPaymentElementMounted ||
      this.bankPaymentElementMounting ||
      !this.bankPaymentElementHost?.nativeElement
    ) {
      return;
    }

    this.bankPaymentElementMounting = true;
    this.mountBankPaymentElement().catch(error => {
      console.error('Unable to mount bank payment element', error);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Unable to load bank connection form.',
      });
    }).finally(() => {
      this.bankPaymentElementMounting = false;
    });
  }

  private async mountBankPaymentElement(): Promise<void> {
    try {
      const host = this.bankPaymentElementHost?.nativeElement;
      if (!host) {
        return;
      }
      const clientSecret = await this.resolveClientSecret();
      this.elements = this.stripeService.getElements(undefined, clientSecret);
      this.bankPaymentElement = this.elements.create('payment', {
        paymentMethodOrder: ['us_bank_account'],
        fields: {
          billingDetails: {
            name: 'never',
            email: 'never',
          },
        },
      });
      this.bankPaymentElement.on('change', (event: any) => {
        this.hasBankElementInput = !event?.empty;
        this.isBankElementComplete = !!event?.complete;
        this.bankConnectionErrorMessage = '';
      });
      this.bankPaymentElement.mount(host);
      this.bankPaymentElementMounted = true;
    } catch (error) {
      console.error('Unable to mount bank payment element', error);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Unable to load bank connection form.',
      });
    }
  }

  private unmountBankElement(): void {
    if (!this.bankPaymentElementMounted || !this.bankPaymentElement) {
      return;
    }

    try {
      this.bankPaymentElement.unmount();
    } catch {
      // no-op
    }

    this.bankPaymentElementMounted = false;
    this.bankPaymentElementMounting = false;
    this.hasBankElementInput = false;
    this.isBankElementComplete = false;
    this.preparedClientSecret = '';
    this.clearBankErrors();
  }

  private async attachNewPaymentMethodToSelectedTeams(createdPaymentMethodId: string): Promise<void> {
    const targetTeamIds = this.selectedTeamIds.filter(teamId => !!teamId && teamId !== this.activeTeam?.id);
    if (!createdPaymentMethodId) {
      return;
    }

    const userId = this.usersService.getCurrentUserId();
    const sourceTeamMethodRecordId = await this.waitForMethodRecordIdForTeam(
      this.activeTeam?.id,
      createdPaymentMethodId
    );
    if (!sourceTeamMethodRecordId) {
      throw new Error('Unable to locate the created bank connection for the selected team.');
    }

    const sourceTeamMethods = await this.teamsService.getPaymentMethods(this.activeTeam?.id);
    const sourceMethod = (sourceTeamMethods ?? []).find(
      (method: any) => method?.id === sourceTeamMethodRecordId
    );
    const sourceReferenceId = sourceMethod?.bankInfo?.paymentMethodReferenceId || '';
    await this.teamsService.attachPaymentToTeamForCompany(
      this.activeTeam?.id,
      userId,
      sourceTeamMethodRecordId,
      { stripePaymentMethodId: createdPaymentMethodId }
    );
    if (targetTeamIds.length === 0) {
      await this.removeLegacyCompanyBankConnections(createdPaymentMethodId, sourceReferenceId);
      return;
    }
    await Promise.all(
      targetTeamIds.map(async teamId => {
        await this.teamsService.attachPaymentToTeamForCompany(
          teamId,
          userId,
          sourceTeamMethodRecordId,
          { stripePaymentMethodId: createdPaymentMethodId }
        );
      })
    );

    const maxVerifyAttempts = 6;
    for (let attempt = 0; attempt < maxVerifyAttempts; attempt += 1) {
      const missingTeamIds = await this.getMissingAttachedTeamIds(targetTeamIds, createdPaymentMethodId);
      if (missingTeamIds.length === 0) {
        await this.removeLegacyCompanyBankConnections(createdPaymentMethodId, sourceReferenceId);
        return;
      }
      await Promise.all(
        missingTeamIds.map(async teamId => {
          await this.teamsService.attachPaymentToTeamForCompany(
            teamId,
            userId,
            sourceTeamMethodRecordId,
            { stripePaymentMethodId: createdPaymentMethodId }
          );
        })
      );
      await this.delay(400);
    }

    throw new Error('Unable to attach bank connection to all selected team(s).');
  }

  private async waitForMethodRecordIdForTeam(teamId: string, methodKey: string): Promise<string> {
    const maxAttempts = 8;
    const delayMs = 500;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const methodId = await this.resolveMethodRecordIdForTeam(teamId, methodKey);
      if (methodId) {
        return methodId;
      }
      await this.delay(delayMs);
    }
    return '';
  }

  private async resolveMethodRecordIdForTeam(teamId: string, methodKey: string): Promise<string> {
    if (!teamId || !methodKey) {
      return '';
    }

    try {
      const teamMethods = await this.teamsService.getPaymentMethods(teamId);
      const matchingMethod = (teamMethods ?? []).find(
        (method: any) =>
          method?.stripePaymentMethod === methodKey ||
          method?.bankInfo?.paymentMethodReferenceId === methodKey ||
          method?.id === methodKey
      );
      return matchingMethod?.id || '';
    } catch {
      return '';
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async getMissingAttachedTeamIds(
    teamIds: string[],
    createdPaymentMethodId: string
  ): Promise<string[]> {
    const checks = await Promise.all(
      teamIds.map(async teamId => {
        try {
          const methods = await this.teamsService.getPaymentMethods(teamId);
          const hasMethod = (methods ?? []).some(
            (method: any) => method?.stripePaymentMethod === createdPaymentMethodId
          );
          return { teamId, hasMethod };
        } catch {
          return { teamId, hasMethod: false };
        }
      })
    );

    return checks.filter(result => !result.hasMethod).map(result => result.teamId);
  }

  private hasPayrollChanges(): boolean {
    if (!this.activeCompany?.id) {
      return true;
    }

    const current = this.capturePayrollSnapshot();

    if (!this.initialPayrollSnapshot) {
      return true;
    }

    return (
      current.name !== this.initialPayrollSnapshot.name ||
      current.providerSlug !== this.initialPayrollSnapshot.providerSlug ||
      current.companyShortName !== this.initialPayrollSnapshot.companyShortName ||
      current.clientId !== this.initialPayrollSnapshot.clientId ||
      current.clientSecret !== this.initialPayrollSnapshot.clientSecret ||
      current.environment !== this.initialPayrollSnapshot.environment ||
      current.subscriptionKey !== this.initialPayrollSnapshot.subscriptionKey ||
      current.refreshToken !== this.initialPayrollSnapshot.refreshToken ||
      current.legalEntityId !== this.initialPayrollSnapshot.legalEntityId ||
      current.tenantId !== this.initialPayrollSnapshot.tenantId ||
      current.legalEntityName !== this.initialPayrollSnapshot.legalEntityName
    );
  }

  private capturePayrollSnapshot(): PayrollSetupSnapshot {
    const normalize = (value: any) => `${value || ''}`.trim();
    const formValue = this.payrollForm.getRawValue();
    const providerSlug = this.normalizeSetupProviderSlug(formValue.providerSlug);
    const common = {
      clientId: normalize(formValue.clientId),
      clientSecret: normalize(formValue.clientSecret),
      name: normalize(formValue.name),
      providerSlug,
    };
    if (providerSlug === PAYCOR_PROVIDER_SLUG) {
      return {
        ...common,
        companyShortName: '',
        environment: PAYCOR_SANDBOX_ENVIRONMENT,
        legalEntityId: normalize(formValue.legalEntityId),
        legalEntityName: normalize(formValue.legalEntityName),
        refreshToken: normalize(formValue.refreshToken),
        subscriptionKey: normalize(formValue.subscriptionKey),
        tenantId: normalize(formValue.tenantId),
      };
    }

    return {
      ...common,
      companyShortName: normalize(formValue.companyShortName),
      environment: '',
      legalEntityId: '',
      legalEntityName: '',
      refreshToken: '',
      subscriptionKey: '',
      tenantId: '',
    };
  }

  private normalizeSetupProviderSlug(value: any): string {
    const normalized = `${value || ''}`.trim().toLowerCase().replace(/[-\s]+/g, '_');
    return normalized === PAYCOR_PROVIDER_SLUG ? PAYCOR_PROVIDER_SLUG : UKG_READY_PROVIDER_SLUG;
  }

  private filterTeamsForCompany(teams: any[], companyId: string): any[] {
    if (!Array.isArray(teams)) {
      return [];
    }
    const withCompanyId = teams.filter(team => !!team?.companyId);
    if (withCompanyId.length === 0) {
      return teams;
    }
    return teams.filter(team => team?.companyId === companyId);
  }

  private async resolveClientSecret(): Promise<string> {
    if (!this.activeCompany?.id) {
      return '';
    }

    try {
      const bankIntent = await this.teamCompaniesService.getPayrollAdminBankSetupIntent(this.activeCompany.id);
      const bankClientSecret =
        bankIntent?.client_secret ||
        bankIntent?.setupIntent?.client_secret ||
        bankIntent?.clientSecret ||
        '';
      if (bankClientSecret) {
        this.preparedClientSecret = bankClientSecret;
        return bankClientSecret;
      }
    } catch (error: any) {
      if (!(error?.status === 404 || error?.statusCode === 404)) {
        throw error;
      }
    }
    return '';
  }

  get hasExistingBankConnection(): boolean {
    return !!this.data?.hasExistingBankConnection;
  }

  get companyTeamNamesLabel(): string {
    const teamNames = this.companyTeams.map(team => team?.name || team?.id).filter(Boolean);
    return teamNames.length > 0
      ? teamNames.join(', ')
      : 'No teams yet. Future teams will inherit this bank connection automatically.';
  }

  openTeamsSetupFlow(): void {
    this.ref.close({ saved: false });
    void this.router.navigate(['payrolladmin', 'teams'], {
      queryParams: {
        openCreateTeam: '1',
        returnFlow: 'connections-bank',
      },
    });
  }

  private getSaveErrorMessage(error: any): string {
    const errorCode = error?.error?.code || '';
    if (errorCode === 'DUPLICATE_COMPANY' || errorCode === 'DUPLICATE_UKG_CONNECTION') {
      return DUPLICATE_DATA_HELP_MESSAGE;
    }

    if (errorCode === 'PAYCOR_CONNECTION_FIELDS_REQUIRED') {
      return 'Complete all required Paycor sandbox connection fields.';
    }

    if (errorCode === 'PAYCOR_LEGAL_ENTITY_SELECTION_REQUIRED') {
      return 'Discover and select a Paycor legal entity, or enter a legal entity ID before saving.';
    }

    if (errorCode === 'PAYROLL_PROVIDER_AUTHENTICATION_FAILED') {
      return this.selectedPayrollProviderSlug === UKG_READY_PROVIDER_SLUG
        ? 'UKG Ready could not authenticate with the credentials provided.'
        : 'Paycor could not authenticate with the sandbox credentials provided.';
    }

    if (errorCode === 'PAYROLL_PROVIDER_AUTHORIZATION_FAILED') {
      return 'The UKG Ready credentials are not authorized to read the employee directory.';
    }

    if (errorCode === 'UKG_READY_COMPANY_SHORT_NAME_INVALID') {
      return 'UKG Ready could not find that company short name.';
    }

    if (errorCode === 'UKG_READY_CONNECTION_CONFIGURATION_INVALID') {
      return 'UKG Ready rejected the connection configuration provided.';
    }

    const backendMessage = `${error?.error?.error || ''}`.trim();
    if (backendMessage.includes('help@wallit.app')) {
      return backendMessage;
    }

    return 'Unable to save connection. Please try again.';
  }

  private getPaycorDiscoveryErrorMessage(error: any): string {
    const errorCode = error?.error?.code || '';
    if (errorCode === 'PAYCOR_CONNECTION_FIELDS_REQUIRED') {
      return 'Complete all required Paycor sandbox connection fields before discovering legal entities.';
    }

    if (errorCode === 'PAYROLL_PROVIDER_AUTHENTICATION_FAILED') {
      return 'Paycor could not authenticate with the sandbox credentials provided.';
    }

    const backendMessage = `${error?.error?.error || ''}`.trim();
    return backendMessage || 'Unable to discover Paycor legal entities right now.';
  }

  private getSafeSaveErrorLog(error: any): any {
    return {
      code: error?.error?.code || error?.code || '',
      message: error?.error?.error || error?.message || '',
      status: error?.status || error?.statusCode || '',
    };
  }

  private validateBankSectionBeforeSave(): string {
    this.clearBankErrors();

    const trimmedFullName = `${this.fullNameControl?.value ?? ''}`.trim();
    if (trimmedFullName !== `${this.fullNameControl?.value ?? ''}`) {
      this.fullNameControl?.setValue(trimmedFullName);
    }

    const trimmedEmail = `${this.emailControl?.value ?? ''}`.trim();
    if (trimmedEmail !== `${this.emailControl?.value ?? ''}`) {
      this.emailControl?.setValue(trimmedEmail);
    }

    this.bankContactForm.markAllAsTouched();
    if (this.fullNameControl?.invalid) {
      return 'Enter the full name associated with the bank account.';
    }

    if (this.emailControl?.invalid) {
      return 'Enter a valid email associated with the bank account.';
    }

    if (!this.elements || !this.bankPaymentElement) {
      this.bankConnectionErrorMessage = BANK_FORM_LOADING_MESSAGE;
      return BANK_FORM_LOADING_MESSAGE;
    }

    if (!this.hasBankElementInput || !this.isBankElementComplete) {
      this.bankConnectionErrorMessage = BANK_FORM_INCOMPLETE_MESSAGE;
      return BANK_FORM_INCOMPLETE_MESSAGE;
    }

    return '';
  }

  private clearBankErrors(): void {
    this.bankOwnerErrorMessage = '';
    this.bankConnectionErrorMessage = '';
  }

  private handleBankConnectionError(error: any): void {
    const normalized = this.normalizeBankConnectionError(error);
    this.showBankConnectionError(normalized.message, normalized.ownerError);
  }

  private showBankConnectionError(message: string, ownerError = false): void {
    if (ownerError) {
      this.bankOwnerErrorMessage = message;
    } else {
      this.bankConnectionErrorMessage = message;
    }

    this.snackBar.openFromComponent(SnackBarComponent, {
      data: message,
    });
  }

  private normalizeBankConnectionError(error: any): { message: string; ownerError: boolean } {
    const directError = error?.error || error;
    const param = `${directError?.param || ''}`.toLowerCase();
    const message = `${directError?.message || directError?.error || ''}`.trim();
    const combined = `${param} ${message}`.toLowerCase();

    if (
      combined.includes('billing_details.name') ||
      combined.includes('billing details name') ||
      combined.includes('payment_method_data[billing_details][name]') ||
      combined.includes('account holder name')
    ) {
      return { message: BANK_OWNER_FIX_MESSAGE, ownerError: true };
    }

    if (
      combined.includes('billing_details.email') ||
      combined.includes('billing details email') ||
      combined.includes('payment_method_data[billing_details][email]')
    ) {
      return { message: BANK_OWNER_FIX_MESSAGE, ownerError: true };
    }

    if (
      combined.includes('complete your bank account information') ||
      combined.includes('bank account information') ||
      combined.includes('select a bank account') ||
      combined.includes('payment_method') && combined.includes('missing')
    ) {
      return { message: BANK_FORM_INCOMPLETE_MESSAGE, ownerError: false };
    }

    if (message) {
      return { message, ownerError: false };
    }

    return { message: GENERIC_BANK_CONNECTION_ERROR_MESSAGE, ownerError: false };
  }

  private async removeLegacyCompanyBankConnections(
    stripePaymentMethodId: string,
    paymentMethodReferenceId: string
  ): Promise<void> {
    const removableMethodIds = new Set<string>();
    for (const team of this.companyTeams) {
      try {
        const methods = await this.teamsService.getPaymentMethods(team.id);
        for (const method of methods ?? []) {
          const bankInfo = method?.bankInfo || {};
          const isBankMethod =
            bankInfo?.type === 'us_bank_account' ||
            (!!method?.stripePaymentMethod && !!bankInfo?.paymentMethodReferenceId);
          if (!isBankMethod || !method?.id) {
            continue;
          }
          const isCurrentStripeMethod =
            !!stripePaymentMethodId && method?.stripePaymentMethod === stripePaymentMethodId;
          const isCurrentReferenceMethod =
            !!paymentMethodReferenceId &&
            bankInfo?.paymentMethodReferenceId === paymentMethodReferenceId;
          if (!isCurrentStripeMethod && !isCurrentReferenceMethod) {
            removableMethodIds.add(method.id);
          }
        }
      } catch (error) {
        console.error(`Unable to inspect payment methods for team ${team?.id}`, error);
      }
    }

    for (const methodId of Array.from(removableMethodIds)) {
      try {
        await this.stripeService.removeCard(methodId);
      } catch (error) {
        console.error(`Unable to remove legacy payment method ${methodId}`, error);
      }
    }
  }
}
